import { Injectable } from '@angular/core';
import { FormGroup, FormControl, FormArray } from '@angular/forms';
import {
    NestedFormGroupType,
    FormGroupType,
} from '../../../../../../core/models/form-group-type';
import { EmissionDesignator } from '../../models/emission-designator';
import { EmissionDesignatorFormService } from '../emission-designator-form/emission-designator-form.service';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { FrequencyFormModel } from '../../models/frequency-form-model';
import { ExistingLicenseEmissionDesignatorOperation } from '../../models/existing-license-emission-designator-operation';
import { ActionCode } from '../../../../../../core/models/resources/action-code';
import { BaseEmissionDesignatorOperationFormService } from '../base-emission-designator-operation-form/base-emission-designator-operation-form.service';

@Injectable({
    providedIn: 'root',
})
export class ExistingLicenseEmissionDesignatorOperationFormService extends BaseEmissionDesignatorOperationFormService<ExistingLicenseEmissionDesignatorOperation> {
    constructor(
        protected override readonly emissionDesignatorFormService: EmissionDesignatorFormService,
    ) {
        super(emissionDesignatorFormService);
    }

    getFormDefinition(): FormGroup<
        NestedFormGroupType<ExistingLicenseEmissionDesignatorOperation>
    > {
        return new FormGroup<
            NestedFormGroupType<ExistingLicenseEmissionDesignatorOperation>
        >({
            addEmissionDesignator: new FormControl<string>(''),
            deleteEmissionDesignator: new FormControl<string>(''),
            emissionDesignators: new FormArray<
                FormGroup<FormGroupType<EmissionDesignator>>
            >([]),
        });
    }

    getFilteredDeleteEmissionDesignatorAutoCompleteOptions(
        value: string,
        frequencies: FormGroup<NestedFormGroupType<FrequencyFormModel>>[],
    ): DropdownOption[] {
        let options: EmissionDesignator[] = [];

        const uniqueEmissionDesignatorsFromAllFrequencies =
            this.getUniqueEmissionDesignatorsFromAllFrequencies(
                frequencies,
            ).filter(
                (emissionDesignator) =>
                    emissionDesignator.actionCode === null ||
                    emissionDesignator.actionCode === ActionCode.Add,
            );

        if (value === '') {
            options = uniqueEmissionDesignatorsFromAllFrequencies;
        } else {
            const filterValue = value!.toUpperCase();

            options = uniqueEmissionDesignatorsFromAllFrequencies.filter(
                (option) => option.code!.toUpperCase().includes(filterValue),
            );
        }

        return options.map((option) => {
            return {
                label: option.code!,
                value: option.code!,
            } as DropdownOption;
        });
    }

    getDeleteEmissionDesignatorDropdownOptions(
        frequencies: FormGroup<NestedFormGroupType<FrequencyFormModel>>[],
        formArray: FormArray<FormGroup<FormGroupType<EmissionDesignator>>>,
    ): DropdownOption[] {
        let list = this.getUniqueEmissionDesignatorsFromAllFrequencies(
            frequencies,
        )
            .filter(
                (emissionDesignator) =>
                    emissionDesignator.actionCode === null ||
                    emissionDesignator.actionCode === ActionCode.Add,
            )
            .map((emissionDesignator) => {
                return {
                    label: emissionDesignator.code!,
                    value: emissionDesignator.code!,
                } as DropdownOption;
            });

        formArray.controls
            .filter((control) => control.value.actionCode === ActionCode.Delete)
            .forEach((control) => {
                list =
                    this.emissionDesignatorFormService.getEmissionDesignatorsListWithoutCode(
                        list,
                        control.value.code!,
                    );
            });

        return list;
    }

    getUniqueEmissionDesignatorsFromAllFrequencies(
        frequencies: FormGroup<NestedFormGroupType<FrequencyFormModel>>[],
    ): EmissionDesignator[] {
        const emissionDesignatorsFromAllFrequenciesSelected =
            this.emissionDesignatorFormService.getEmissionDesignatorsFromAllFrequencies(
                frequencies,
            );

        // Priority is given to emission designators with action code "Add" or null, so they are sorted to the front of the list before filtering for unique codes.
        const isPriority = (v: EmissionDesignator) =>
            v.actionCode === ActionCode.Add || v.actionCode == null;

        const uniqueByCode = new Map<string | null, EmissionDesignator>();

        for (const list of emissionDesignatorsFromAllFrequenciesSelected) {
            for (const emissionDesignator of list) {
                const existing = uniqueByCode.get(emissionDesignator.code);
                if (
                    existing == null ||
                    (!isPriority(existing) && isPriority(emissionDesignator))
                ) {
                    uniqueByCode.set(
                        emissionDesignator.code,
                        emissionDesignator,
                    );
                }
            }
        }

        return Array.from(uniqueByCode.values());
    }
}
